<script setup lang="ts">
  import { toRef, ref, watch, computed, onMounted, onUnmounted } from 'vue'
  import ExitTimer from '../exit-timer/ExitTimer.vue'
  import { IonModal } from '@ionic/vue'
  import SecondaryActionButton from '@/components/buttons/SecondaryActionButton.vue'
  import ActionButtonWrapper from '../buttons/loading-action-button/ActionButtonWrapper.vue'
  import PrimaryActionButton from '../buttons/PrimaryActionButton.vue'
  import { useKeyboard } from '@/composables/useKeyboard'
  import { Capacitor } from '@capacitor/core'

  interface Props {
    hasTimer?: boolean
    showConfirmButtons?: boolean
    fullscreen?: boolean
    dismissButtonLabel?: string
    confirmButtonLabel?: string
    noPadding?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    showConfirmButtons: false,
    fullscreen: false,
    dismissButtonLabel: undefined,
    confirmButtonLabel: undefined,
    noPadding: false,
  })
  const emit = defineEmits<{
    closed: []
    opened: []
    dismiss: []
    confirm: []
    willOpen: []
    didOpen: []
    willDismiss: []
    didDismiss: []
    clickedOutside: []
  }>()

  const showModal = ref(false)
  const modal = ref<InstanceType<typeof IonModal>>()
  const contentWrapper = ref<HTMLElement>()
  const hasTimer = toRef(props, 'hasTimer')
  const showConfirmButtons = toRef(props, 'showConfirmButtons')
  // eslint-disable-next-line
  const timer = ref<InstanceType<typeof ExitTimer> | null>(null)

  // Use keyboard composable for better architecture
  const { addShowListener, addHideListener } = useKeyboard()

  // Read-only computed property to reflect modal state
  const isOpen = computed(() => showModal.value)

  // Computed property for modal CSS class
  const modalClass = computed(() =>
    props.fullscreen ? 'modal-fullscreen' : 'modal-centered',
  )

  const scrollToFocusedInput = () => {
    if (!contentWrapper.value) return

    // Find the currently focused element
    const focusedElement = document.activeElement as HTMLElement
    if (
      !focusedElement ||
      !['INPUT', 'TEXTAREA', 'SELECT'].includes(focusedElement.tagName)
    ) {
      return
    }

    // Check if the focused element is within our modal
    if (!contentWrapper.value.contains(focusedElement)) {
      return
    }

    // Get the position of the focused element relative to the content wrapper
    const focusedRect = focusedElement.getBoundingClientRect()
    const wrapperRect = contentWrapper.value.getBoundingClientRect()

    // Calculate the relative position within the scrollable container
    const relativeTop =
      focusedRect.top - wrapperRect.top + contentWrapper.value.scrollTop

    // Scroll to position the input in the upper third of the visible area
    const targetScrollTop = Math.max(0, relativeTop - wrapperRect.height / 3)

    console.debug(
      '[BaseModal] Scrolling to focused input at position:',
      targetScrollTop,
    )

    // Smooth scroll to the calculated position
    contentWrapper.value.scrollTo({
      top: targetScrollTop,
      behavior: 'smooth',
    })
  }

  // Store cleanup functions
  let removeShowListener: (() => void) | null = null
  let removeHideListener: (() => void) | null = null

  // Setup keyboard handling when component mounts
  onMounted(() => {
    if (Capacitor.isNativePlatform()) {
      // Add keyboard listeners using the composable
      removeShowListener = addShowListener((info) => {
        console.debug(
          '[BaseModal] Keyboard will show, height:',
          info?.keyboardHeight,
        )
        scrollToFocusedInput()
      })

      removeHideListener = addHideListener(() => {
        console.debug('[BaseModal] Keyboard will hide')
      })
    }
  })

  // Cleanup keyboard listeners when component unmounts
  onUnmounted(() => {
    // Clean up only our specific listeners
    removeShowListener?.()
    removeHideListener?.()
  })

  function resetTimer() {
    timer.value?.resetTimer()
  }

  function open() {
    if (hasTimer.value) {
      resetTimer()
    }
    showModal.value = true
  }
  function dismiss() {
    ;(document.activeElement as HTMLInputElement).blur()
    showModal.value = false
    emit('dismiss')
  }
  function autoDismiss() {
    console.debug('[BaseModal] Autodismissed due to timer timeout')
    dismiss()
  }

  async function onWillDismiss() {
    return new Promise((resolve) => {
      if (!showModal.value) {
        // If already closed, resolve immediately
        resolve(null)
        return
      }

      // Watch for the modal to close
      const stop = watch(showModal, (isOpen) => {
        if (!isOpen) {
          resolve(null)
          stop()
        }
      })
    })
  }

  function confirm() {
    emit('confirm')
    ;(document.activeElement as HTMLInputElement).blur()
    showModal.value = false
  }

  function handleWillDismiss(event: CustomEvent) {
    emit('willDismiss')
    // Check if the modal was dismissed by clicking outside (backdrop)
    if (event.detail.role === 'backdrop') {
      emit('clickedOutside')
    }
    showModal.value = false
  }

  watch(showModal, (showModal) => {
    if (showModal) {
      emit('opened')
    } else {
      emit('closed')
    }
  })

  defineExpose({
    open,
    close: dismiss, //@deprecated
    dismiss,
    resetTimer,
    onWillDismiss,
    isOpen,
  })
</script>
<template>
  <ion-modal
    ref="modal"
    :is-open="showModal"
    :class="modalClass"
    @will-present="() => emit('willOpen')"
    @did-present="() => emit('didOpen')"
    @did-dismiss="() => emit('didDismiss')"
    @will-dismiss="handleWillDismiss"
  >
    <div
      id="base-content"
      :class="modalClass"
      class="flex justify-center flex-col w-full items-center relative rounded-xl bg-white"
      @click="resetTimer()"
      @mousemove="resetTimer()"
      @touchstart="resetTimer()"
      @wheel="resetTimer()"
      @keydown="resetTimer()"
    >
      <div id="head-bar-wrapper" class="relative w-full pb-2 mt-2 px-3">
        <div
          v-if="$slots.leftButton"
          class="absolute top-0 left-3 h-10 flex items-center"
        >
          <slot name="leftButton"></slot>
        </div>
        <div
          class="font-bold text-lg flex justify-center items-center h-10 w-full"
        >
          <slot name="title"></slot>
        </div>
        <div class="absolute top-0 right-3 h-10 flex items-center">
          <exit-timer
            v-if="hasTimer"
            ref="timer"
            appium="exit-timer-base-modal"
            @click.stop="dismiss"
            @mousemove.stop
            @touchstart.stop
            @wheel.stop
            @keydown.stop
            @timeout="autoDismiss"
          />
          <button
            v-else
            id="close-modal"
            appium="close-base-modal"
            class="active:outline-none focus:outline-none flex"
            @click="dismiss"
          >
            <font-awesome-icon :icon="['fas', 'xmark']" class="!text-lg" />
          </button>
        </div>
      </div>

      <div
        id="base-content-wrapper"
        ref="contentWrapper"
        :class="{ 'py-3 px-3': !props.noPadding }"
      >
        <slot></slot>
      </div>

      <div
        v-if="showConfirmButtons"
        id="confirm-buttons"
        class="grid-2 button-top-margin gap-4 flex py-2 px-3 w-full"
      >
        <action-button-wrapper>
          <secondary-action-button @click="dismiss">
            <slot name="dismissalButtonText">{{
              props.dismissButtonLabel || $t('words.dismiss')
            }}</slot>
          </secondary-action-button>
        </action-button-wrapper>

        <action-button-wrapper>
          <primary-action-button appium="submitButtonModal" @click="confirm">
            {{ props.confirmButtonLabel || $t('words.finish') }}
          </primary-action-button>
        </action-button-wrapper>
      </div>
    </div>
  </ion-modal>
</template>
<style scoped>
  #base-content {
    position: relative;
  }
  #base-content.modal-centered {
    max-height: calc(
      100vh - 1.5rem - var(--ion-safe-area-top, 0) -
        var(--ion-safe-area-bottom, 0)
    );
  }
  #base-content.modal-fullscreen {
    padding-left: var(--ion-safe-area-left, 0) !important;
    padding-right: var(--ion-safe-area-right, 0) !important;
    padding-top: var(--ion-safe-area-top, 0) !important;
    padding-bottom: var(--ion-safe-area-bottom, 0) !important;
  }

  #base-content-wrapper {
    max-height: max-content;
    height: fit-content;
    width: 100%;
    overflow-y: auto;
    position: relative;
  }

  #head-bar-wrapper {
    border-bottom: 1px solid #eeeeee;
  }

  #confirm-buttons {
    border-top: 1px solid #eeeeee;
  }
</style>
